import { ChangeDetectionStrategy, Component, input } from '@angular/core';

export interface ReviewListItem {
  readonly id: string;
  readonly rating: number;
  readonly comment: string;
  readonly authorLabel: string;
  readonly experienceAt: string;
  readonly publishedAt: string;
  readonly producerReply?: string | null;
  readonly producerReplyAt?: string | null;
}

/**
 * Liste d'avis publiés, triés chronologiquement par l'API.
 *
 * Les dates d'expérience et de publication sont affichées séparément, conformément
 * à la charte des avis.
 */
@Component({
  selector: 'ct-review-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  template: `
    @if (reviews().length === 0) {
      <p class="empty">{{ emptyLabel() }}</p>
    } @else {
      <ul class="list">
        @for (review of reviews(); track review.id) {
          <li class="item">
            <div class="head">
              <span class="stars" [attr.aria-label]="review.rating + ' sur 5'">
                @for (star of stars(review.rating); track $index) {
                  <span aria-hidden="true">&#9733;</span>
                }
              </span>
              <span class="meta">
                {{ review.authorLabel }} · expérience le {{ formatDate(review.experienceAt) }}
              </span>
            </div>
            <p class="comment">{{ review.comment }}</p>
            <p class="published">Publié le {{ formatDate(review.publishedAt) }}</p>
            @if (review.producerReply; as reply) {
              <blockquote class="reply">
                <strong>Réponse du producteur</strong>
                <p>{{ reply }}</p>
                @if (review.producerReplyAt; as repliedAt) {
                  <footer>{{ formatDate(repliedAt) }}</footer>
                }
              </blockquote>
            }
          </li>
        }
      </ul>
    }
  `,
  styles: `
    .empty {
      color: var(--ct-text-muted);
      font-size: var(--ct-size-sm);
    }

    .list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .item {
      padding-bottom: 1rem;
      border-bottom: 1px solid var(--ct-border-subtle);
    }

    .head {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 0.5rem;
      margin-bottom: 0.35rem;
    }

    .stars {
      color: var(--ct-or-recolte-dark);
      letter-spacing: 0.05em;
    }

    .meta,
    .published {
      font-size: var(--ct-size-sm);
      color: var(--ct-text-muted);
    }

    .comment {
      margin: 0.25rem 0;
    }

    .reply {
      margin: 0.75rem 0 0;
      padding: 0.75rem 1rem;
      border-left: 3px solid var(--ct-or-recolte);
      background: var(--ct-surface-muted);
      font-size: var(--ct-size-sm);
    }

    .reply p {
      margin: 0.35rem 0 0;
    }

    .reply footer {
      margin-top: 0.35rem;
      color: var(--ct-text-muted);
    }
  `,
})
export class CtReviewList {
  readonly reviews = input.required<readonly ReviewListItem[]>();
  readonly emptyLabel = input('Pas encore d’avis publié.');

  protected stars(rating: number): readonly number[] {
    return Array.from({ length: Math.max(0, Math.min(5, Math.round(rating))) }, (_, index) => index);
  }

  protected formatDate(iso: string): string {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(iso));
  }
}
